/**
 * pages.ts — หน้าจอ SPA: Dashboard, รายงาน P&L (drill-down), ฟอร์ม, Login
 * ข้อมูลมาจาก view (demo หรือ live จาก API)
 */
import type { PnlLine, PnlLineKey } from '@rrp/shared';
import { PNL_CATEGORY_OPTIONS } from '@rrp/shared';
import type { Page } from './router.js';
import { formatTHB, fmtPct, fmtDate, deltaHtml } from './format.js';
import { getView } from './view.js';
import {
  getFilters,
  setFilters,
  presetRange,
  rangeLabel,
  CHANNEL_OPTIONS,
  COMPARE_OPTIONS,
  type ComparisonType,
} from './filters.js';
import { signIn, signUp, resetPassword, updatePassword, type AuthHash } from './auth.js';
import { getTenant } from './state.js';
import { exportPnlXlsx, exportPnlSvg, drilldown, createEntry, onboardingApi } from './api.js';
import { svgToPngBlob, downloadBlob } from './png-export.js';
import { pdfFromSvg } from './pdf-export.js';
import { pnlToSvg } from '@rrp/shared';
import { esc } from './ui.js';
import { config } from './config.js';

export function amountOf(lines: PnlLine[], key: PnlLineKey): number {
  const a = lines.find((l) => l.key === key)?.amount;
  return a === null || a === undefined ? 0 : Number(a);
}
export function pctOf(lines: PnlLine[], key: PnlLineKey): number | null {
  return lines.find((l) => l.key === key)?.percent ?? null;
}

/**
 * บรรทัดที่เจาะดูรายการต้นทางได้ในโหมดข้อมูลจริง (ตรงกับที่ `/api/pnl/drilldown` รองรับ)
 * โหมด demo ใช้ข้อมูลตัวอย่างในเครื่องจึงเช็คจากจำนวนแถวที่มีแทน
 */
const DRILLABLE_KEYS = new Set<PnlLineKey>([
  'sales',
  'other_income',
  'cost_of_products',
  'marketing_expenses',
  'commissions_platform_fees',
  'transportation_fulfillment',
  'payroll_employee',
  'accounting_professional',
  'fixed_cost',
  'variable_cost',
  'other_operating',
  'depreciation_amortization',
  'interest_finance',
  'corporate_income_tax',
]);

const TOTAL_KEYS = new Set<PnlLineKey>(['total_income', 'total_costs_of_sale', 'total_sga']);
const EMPH_KEYS = new Set<PnlLineKey>([
  'gross_margin',
  'ebitda',
  'ebit',
  'profit_before_tax',
  'net_profit',
]);

function modeNote(): string {
  const v = getView();
  return v.mode === 'demo'
    ? `<span class="pill" title="ยังไม่ต่อ API">โหมด demo</span>`
    : `<span class="pill active" title="ข้อมูลจาก API จริง">🟢 ข้อมูลจริง</span>`;
}

export function bannerHtml(): string {
  const c = getView().completeness;
  if (c.status === 'complete') return '';
  return `<div class="banner"><span>⚠️</span><div><strong>ข้อมูลยังไม่สมบูรณ์</strong> — มี SKU ไม่มีต้นทุน ${c.missingCostCount} รายการ (มูลค่าขายที่กระทบ ${formatTHB(c.affectedRevenue)}) กรุณากรอกต้นทุนเพื่อให้กำไรถูกต้อง</div></div>`;
}

/** แถบตัวกรองกลาง — ช่วงวันที่ พรีเซ็ต ช่องทาง และงวดเปรียบเทียบ (T083) */
function filterBarHtml(): string {
  const f = getFilters();
  const presets: [string, string][] = [
    ['today', 'วันนี้'],
    ['last7', '7 วัน'],
    ['last30', '30 วัน'],
    ['this_month', 'เดือนนี้'],
    ['prev_month', 'เดือนก่อน'],
    ['this_year', 'ปีนี้'],
  ];
  return `<form class="filter-bar" id="filter-bar">
    <div class="preset-row">${presets
      .map(
        ([key, label]) =>
          `<button type="button" class="pill preset" data-preset="${key}">${label}</button>`,
      )
      .join('')}</div>
    <label>ตั้งแต่<input type="date" name="from" value="${f.from}"></label>
    <label>ถึง<input type="date" name="to" value="${f.to}"></label>
    <label>ช่องทาง<select name="channelCode">${CHANNEL_OPTIONS.map(
      ([v, l]) => `<option value="${v}"${v === f.channelCode ? ' selected' : ''}>${l}</option>`,
    ).join('')}</select></label>
    <label>เปรียบเทียบ<select name="compare">${COMPARE_OPTIONS.map(
      ([v, l]) => `<option value="${v}"${v === f.compare ? ' selected' : ''}>${l}</option>`,
    ).join('')}</select></label>
    <button type="submit" class="pill active">ใช้ตัวกรอง</button>
  </form>`;
}

function wireFilterBar(root: HTMLElement): void {
  const form = root.querySelector<HTMLFormElement>('#filter-bar');
  if (!form) return;
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const value = (name: string): string =>
      (form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement).value;
    setFilters({
      from: value('from'),
      to: value('to'),
      channelCode: value('channelCode'),
      compare: value('compare') as ComparisonType,
    });
  });
  form.querySelectorAll<HTMLButtonElement>('button[data-preset]').forEach((b) =>
    b.addEventListener('click', () => setFilters(presetRange(b.dataset['preset']!))),
  );
}

/** กราฟยอดขายรายวันของช่วงที่เลือก (ข้อมูลจริงในโหมด live) */
function chartHtml(): string {
  const series = getView().daily.map((p) => ({ date: p.date, value: Number(p.sales) }));
  if (series.length === 0) return '<p class="muted">ไม่มีข้อมูลในช่วงที่เลือก</p>';
  const max = Math.max(...series.map((s) => Math.abs(s.value)), 1);
  const w = 100 / series.length;
  const bars = series
    .map((s, i) => {
      const h = (Math.abs(s.value) / max) * 90;
      const y = s.value >= 0 ? 100 - h : 100;
      return `<rect class="bar ${s.value < 0 ? 'neg' : ''}" x="${(i * w + 0.6).toFixed(2)}" y="${y.toFixed(2)}" width="${Math.max(w - 1.2, 0.4).toFixed(2)}" height="${h.toFixed(2)}"><title>${s.date}: ${formatTHB(s.value)}</title></rect>`;
    })
    .join('');
  return `<svg class="chart" viewBox="0 0 100 100" preserveAspectRatio="none" role="img" aria-label="ยอดขายรายวัน">${bars}</svg>`;
}

const CHANNEL_LABELS: Record<string, string> = {
  tiktok_shop: 'TikTok Shop',
  shopee: 'Shopee',
  lazada: 'Lazada',
  facebook: 'Facebook',
  line: 'LINE',
};

/** แถบสัดส่วน (ใช้ทั้งสัดส่วนช่องทางและสัดส่วนค่าใช้จ่าย) */
function mixHtml(items: { label: string; value: number }[], empty: string): string {
  const positive = items.filter((i) => i.value > 0);
  const total = positive.reduce((s, i) => s + i.value, 0);
  if (total <= 0) return `<p class="muted">${empty}</p>`;
  return `<div class="mix">${positive
    .map((i) => {
      const pct = (i.value / total) * 100;
      return `<div class="mix-row">
        <div class="mix-label">${i.label}</div>
        <div class="mix-track"><div class="mix-fill" style="width:${pct.toFixed(1)}%"></div></div>
        <div class="mix-value">${formatTHB(i.value)} <span class="muted">(${pct.toFixed(1)}%)</span></div>
      </div>`;
    })
    .join('')}</div>`;
}

/** สัดส่วนยอดขายตามช่องทาง (T086) */
export function channelMixHtml(): string {
  const items = getView().channelMix.map((c) => ({
    label: CHANNEL_LABELS[c.channelCode] ?? c.channelCode,
    value: Number(c.sales),
  }));
  return mixHtml(items, 'ยังไม่มียอดขายที่รับรู้ในช่วงนี้');
}

/** สัดส่วนค่าใช้จ่ายตามหมวด P&L (T086) — ใช้บรรทัดเดียวกับรายงาน */
function expenseMixHtml(): string {
  const keys: PnlLineKey[] = [
    'cost_of_products',
    'marketing_expenses',
    'commissions_platform_fees',
    'transportation_fulfillment',
    'payroll_employee',
    'accounting_professional',
    'fixed_cost',
    'variable_cost',
    'other_operating',
    'depreciation_amortization',
    'interest_finance',
  ];
  const lines = getView().lines;
  const items = keys.map((k) => {
    const line = lines.find((l) => l.key === k);
    return { label: line?.label ?? k, value: Number(line?.amount ?? 0) };
  });
  return mixHtml(items, 'ยังไม่มีค่าใช้จ่ายในช่วงนี้');
}

function kpiHtml(): string {
  const v = getView();
  const cards = [
    { label: 'ยอดขาย (Sales)', key: 'sales' as PnlLineKey, sub: '' },
    { label: 'รายได้รวม', key: 'total_income' as PnlLineKey, sub: '' },
    { label: 'กำไรขั้นต้น', key: 'gross_margin' as PnlLineKey, sub: `GP% ${fmtPct(pctOf(v.lines, 'gp_pct'))}` },
    { label: 'EBITDA', key: 'ebitda' as PnlLineKey, sub: fmtPct(pctOf(v.lines, 'ebitda_pct')) },
    { label: 'กำไรสุทธิ', key: 'net_profit' as PnlLineKey, sub: `NP% ${fmtPct(pctOf(v.lines, 'np_pct'))}` },
  ];
  return cards
    .map((c) => {
      const val = amountOf(v.lines, c.key);
      const compareLabel =
        COMPARE_OPTIONS.find(([key]) => key === getFilters().compare)?.[1] ?? 'เทียบงวดก่อน';
      return `<div class="kpi"><div class="label">${c.label}${c.sub ? ` · ${c.sub}` : ''}</div><div class="value">${formatTHB(val)}</div>${deltaHtml(val, v.prevAmounts[c.key] ?? 0, compareLabel)}</div>`;
    })
    .join('');
}

function pnlTableHtml(clickable: boolean): string {
  const v = getView();
  const rows = v.lines
    .map((l) => {
      if (l.kind === 'header') return `<tr class="header"><td colspan="2">${l.label}</td></tr>`;
      if (l.kind === 'percent')
        return `<tr class="pct"><td>${l.label}</td><td class="num">${fmtPct(l.percent)}</td></tr>`;
      const n = Number(l.amount);
      const cls = TOTAL_KEYS.has(l.key) ? 'total' : EMPH_KEYS.has(l.key) ? 'emph' : '';
      const numCls = n < 0 ? 'num neg-num' : 'num';
      // live: เจาะได้ทุกบรรทัดที่ API รองรับ (ข้อมูลอยู่ฝั่ง server) — demo: เช็คจากข้อมูลตัวอย่าง
      const drill =
        clickable &&
        (v.mode === 'live' ? DRILLABLE_KEYS.has(l.key) : v.drilldown(l.key).length > 0);
      const attr = drill ? ` class="drill ${cls}" data-key="${l.key}" tabindex="0" role="button"` : ` class="${cls}"`;
      return `<tr${attr}><td>${l.label}${drill ? ' 🔍' : ''}</td><td class="${numCls}">${formatTHB(l.amount ?? '0')}</td></tr>`;
    })
    .join('');
  return `<div class="table-scroll"><table class="pnl-table" data-testid="pnl-table">${rows}</table></div>`;
}

export const dashboardPage: Page = {
  title: 'แดชบอร์ดภาพรวม',
  render() {
    const v = getView();
    return `
      <div class="topbar"><h1 data-testid="app-title">แดชบอร์ดภาพรวม</h1>
        <div class="filters">${modeNote()}<span class="pill active">${rangeLabel(v.range)}</span></div>
      </div>
      ${filterBarHtml()}
      ${bannerHtml()}
      <div class="kpis" data-testid="kpis">${kpiHtml()}</div>
      <div class="card"><h3>ยอดขายรายวัน (${rangeLabel(v.range)})</h3>${chartHtml()}</div>
      <div class="grid2-cards">
        <div class="card"><h3>สัดส่วนยอดขายตามช่องทาง</h3>${channelMixHtml()}</div>
        <div class="card"><h3>สัดส่วนต้นทุนและค่าใช้จ่าย</h3>${expenseMixHtml()}</div>
      </div>
      <div class="card"><h3>งบกำไรขาดทุน (P&amp;L)</h3>${pnlTableHtml(false)}
        <p class="foot-note">เจาะรายการที่ <a href="#pnl">หน้ารายงาน P&amp;L →</a></p></div>`;
  },
  mount(root) {
    wireFilterBar(root);
  },
};

export const pnlPage: Page = {
  title: 'รายงานกำไรขาดทุน (P&L)',
  render() {
    return `
      <div class="topbar"><h1>รายงานกำไรขาดทุน (P&amp;L)</h1><div class="filters">${modeNote()}<span class="pill active">${rangeLabel(getView().range)}</span><button id="export-xlsx" class="mini" title="ดาวน์โหลด Excel">⬇ Excel</button><button id="export-png" class="mini" title="ดาวน์โหลดภาพรายงาน">⬇ PNG</button><button id="export-pdf" class="mini" title="ดาวน์โหลด PDF">⬇ PDF</button></div></div>
      ${filterBarHtml()}
      ${bannerHtml()}
      <div class="card"><h3>งบกำไรขาดทุน — 26 บรรทัด (คลิกบรรทัด 🔍 เพื่อเจาะรายการ)</h3>${pnlTableHtml(true)}</div>
      <div id="drawer" class="drawer" hidden></div>`;
  },
  mount(root) {
    wireFilterBar(root);
    const v = getView();
    const range = v.range;
    const fileStem = `pnl-${range.from}_${range.to}`;
    const drawer = root.querySelector<HTMLElement>('#drawer')!;
    const exportBtn = root.querySelector<HTMLButtonElement>('#export-xlsx');
    exportBtn?.addEventListener('click', async () => {
      if (!getTenant()) {
        alert('ส่งออก Excel ใช้ได้ในโหมดข้อมูลจริง');
        return;
      }
      exportBtn.disabled = true;
      exportBtn.textContent = 'กำลังส่งออก…';
      try {
        const blob = await exportPnlXlsx(range.from, range.to);
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${fileStem}.xlsx`;
        a.click();
        URL.revokeObjectURL(a.href);
      } catch (e) {
        alert((e as Error).message);
      } finally {
        exportBtn.disabled = false;
        exportBtn.textContent = '⬇ Excel';
      }
    });
    // T102/T103 — ภาพรายงาน: live ใช้ SVG จาก API (payload เดียวกับหน้าจอ), demo วาดจาก view ปัจจุบัน
    const reportSvg = async (): Promise<string> =>
      getTenant()
        ? await exportPnlSvg(range.from, range.to)
        : pnlToSvg(
            {
              lines: v.lines,
              range,
              completeness: v.completeness,
            },
            {
              generatedAt: new Date().toISOString(),
              accountName: 'ข้อมูลตัวอย่าง (demo)',
              filters: [{ label: 'งวด', value: rangeLabel(range) }],
            },
          );

    const pngBtn = root.querySelector<HTMLButtonElement>('#export-png');
    pngBtn?.addEventListener('click', async () => {
      pngBtn.disabled = true;
      pngBtn.textContent = 'กำลังสร้างภาพ…';
      try {
        downloadBlob(await svgToPngBlob(await reportSvg()), `${fileStem}.png`);
      } catch (e) {
        alert(e instanceof Error ? e.message : 'สร้างภาพไม่สำเร็จ');
      } finally {
        pngBtn.disabled = false;
        pngBtn.textContent = '⬇ PNG';
      }
    });

    // T103 — PDF: ใช้ SVG ชุดเดียวกับ PNG แล้วฝังเป็นหน้า A4 (ฟอนต์ไทยถูกต้อง 100%)
    const pdfBtn = root.querySelector<HTMLButtonElement>('#export-pdf');
    pdfBtn?.addEventListener('click', async () => {
      pdfBtn.disabled = true;
      pdfBtn.textContent = 'กำลังสร้าง PDF…';
      try {
        downloadBlob(await pdfFromSvg(await reportSvg()), `${fileStem}.pdf`);
      } catch (e) {
        alert(e instanceof Error ? e.message : 'สร้าง PDF ไม่สำเร็จ');
      } finally {
        pdfBtn.disabled = false;
        pdfBtn.textContent = '⬇ PDF';
      }
    });

    const renderDrawer = (
      title: string,
      count: number,
      total: string,
      rows: { date: string | null; label: string; amount: string }[],
    ): void => {
      const body = rows
        .map(
          (r) =>
            `<tr><td>${fmtDate(r.date ?? '')}</td><td>${r.label}</td><td class="num ${Number(r.amount) < 0 ? 'neg-num' : ''}">${formatTHB(r.amount)}</td></tr>`,
        )
        .join('');
      drawer.innerHTML = `<div class="drawer-head"><strong>${title}</strong> — ${count} รายการ (รวม ${formatTHB(total)}) <button id="drawer-close" class="pill">ปิด</button></div><div class="table-scroll"><table class="pnl-table"><tr class="header"><td>วันที่</td><td>รายการ</td><td class="num">จำนวน</td></tr>${body}</table></div>`;
      drawer.hidden = false;
      drawer.querySelector('#drawer-close')?.addEventListener('click', () => (drawer.hidden = true));
    };
    const open = async (key: string): Promise<void> => {
      const line = v.lines.find((l) => l.key === key);
      const label = line?.label ?? key;
      if (getTenant()) {
        // live: เจาะจาก API (subtotal ตรงกับบรรทัด)
        drawer.innerHTML = `<div class="drawer-head"><strong>${label}</strong> — กำลังโหลด… <button id="drawer-close" class="pill">ปิด</button></div>`;
        drawer.hidden = false;
        drawer.querySelector('#drawer-close')?.addEventListener('click', () => (drawer.hidden = true));
        try {
          const d = await drilldown(key, range.from, range.to);
          renderDrawer(label, d.count, d.total, d.items);
        } catch (e) {
          drawer.innerHTML = `<div class="drawer-head"><strong>${label}</strong> <button id="drawer-close" class="pill">ปิด</button></div><p class="error" style="padding:12px">${(e as Error).message}</p>`;
          drawer.querySelector('#drawer-close')?.addEventListener('click', () => (drawer.hidden = true));
        }
        return;
      }
      const rows = v.drilldown(key as PnlLineKey);
      renderDrawer(label, rows.length, line?.amount ?? '0', rows);
    };
    root.querySelectorAll<HTMLElement>('tr.drill').forEach((tr) => {
      tr.addEventListener('click', () => void open(tr.dataset['key']!));
      tr.addEventListener('keydown', (e) => {
        if ((e as KeyboardEvent).key === 'Enter') void open(tr.dataset['key']!);
      });
    });
  },
};

// value=อังกฤษ (เก็บใน DB — engine bucket ตามนี้), แสดงเป็นไทย
const EXPENSE_CATEGORY_OPTIONS = PNL_CATEGORY_OPTIONS.map(
  (o) => `<option value="${o.value}">${o.label}</option>`,
).join('');

/**
 * T089 — ฟอร์มกลางรายได้/ค่าใช้จ่าย (dynamic fields ตามประเภทรายการ)
 * โหมดข้อมูลจริงบันทึกผ่าน POST /api/entries; โหมด demo แสดงตัวอย่างการคำนวณ
 */
export const expenseFormPage: Page = {
  title: 'กรอกรายได้/ค่าใช้จ่าย',
  render() {
    return `
      <div class="topbar"><h1>กรอกรายได้/ค่าใช้จ่าย</h1></div>
      <div class="card" style="max-width:900px">
        <form id="entry-form" class="grid-form">
          <label>ประเภทรายการ
            <select name="classification" id="f-class">
              <option value="expense">ค่าใช้จ่าย</option>
              <option value="income">รายได้อื่น</option>
              <option value="tax_only">ทะเบียนภาษี (VAT/WHT)</option>
              <option value="asset">ทรัพย์สิน</option>
              <option value="adjustment">รายการปรับปรุง</option>
              <option value="reversal">กลับรายการ</option>
            </select>
          </label>
          <label id="cat-wrap">หมวด P&amp;L
            <select name="pnlCategory" id="f-cat">${EXPENSE_CATEGORY_OPTIONS}</select>
          </label>
          <label>วันที่เอกสาร<input type="date" name="documentDate" /></label>
          <label>วันที่เกิดรายการ<input type="date" name="eventDate" value="2025-04-15" /></label>
          <label>วันที่ลงบัญชี<input type="date" name="accountingDate" /></label>
          <label>วันที่จ่ายจริง<input type="date" name="paymentDate" /></label>

          <label class="cp-field">คู่ค้า<input type="text" name="counterpartyName" placeholder="ชื่อผู้ขาย/ผู้รับเงิน" /></label>
          <label class="cp-field">ประเภทคู่ค้า
            <select name="counterpartyType"><option value="">—</option><option value="juristic">นิติบุคคล</option><option value="individual">บุคคลธรรมดา</option></select>
          </label>
          <label class="cp-field">เลขผู้เสียภาษี<input type="text" name="counterpartyTaxId" placeholder="0000000000000" /></label>
          <label class="cp-field">สาขา<input type="text" name="counterpartyBranch" placeholder="00000" /></label>

          <label class="doc-field">ประเภทเอกสาร
            <select name="documentType"><option value="">—</option><option value="tax_invoice">ใบกำกับภาษี</option><option value="receipt">ใบเสร็จ</option><option value="credit_note">ใบลดหนี้</option><option value="debit_note">ใบเพิ่มหนี้</option><option value="other">อื่น ๆ</option></select>
          </label>
          <label class="doc-field">เลขใบกำกับ<input type="text" name="invoiceNo" /></label>
          <label class="doc-field">เลขใบเสร็จ<input type="text" name="receiptNo" /></label>

          <label>ยอดก่อน VAT<input type="text" inputmode="decimal" name="amountBeforeVat" id="f-base" value="0" /></label>
          <label>VAT<input type="text" inputmode="decimal" name="vatAmount" id="f-vat" value="0" /></label>
          <label>หัก ณ ที่จ่าย (WHT)<input type="text" inputmode="decimal" name="whtAmount" id="f-wht" value="0" /></label>
          <label>ยอดรวม<input type="text" name="totalAmount" id="f-total" readonly /></label>
          <label>จ่ายจริงแล้ว<input type="text" inputmode="decimal" name="paidAmount" id="f-paid-amt" value="0" /></label>

          <label class="tax-field">ฐาน VAT
            <select name="vatBasis"><option value="">—</option><option value="exclusive">แยกนอก</option><option value="inclusive">รวมใน</option><option value="zero_rated">อัตราศูนย์</option><option value="exempt">ยกเว้น</option><option value="none">ไม่มี</option></select>
          </label>
          <label class="tax-field">ขอคืน VAT ได้
            <select name="vatClaimability"><option value="">—</option><option value="claimable">ขอคืนได้</option><option value="non_claimable">ขอคืนไม่ได้</option><option value="partial">บางส่วน</option><option value="na">ไม่เกี่ยวข้อง</option></select>
          </label>
          <label class="tax-field">ประเภท WHT
            <select name="whtType"><option value="">—</option><option value="pnd1">ภ.ง.ด.1</option><option value="pnd3">ภ.ง.ด.3</option><option value="pnd53">ภ.ง.ด.53</option><option value="none">ไม่มี</option></select>
          </label>
          <label class="tax-field">อัตรา WHT (%)<input type="text" inputmode="decimal" name="whtRate" placeholder="3" /></label>

          <label class="pay-field">วิธีชำระ
            <select name="paymentMethod"><option value="">—</option><option value="bank_transfer">โอนธนาคาร</option><option value="cash">เงินสด</option><option value="credit_card">บัตรเครดิต</option><option value="promptpay">พร้อมเพย์</option><option value="unpaid">ยังไม่จ่าย</option><option value="other">อื่น ๆ</option></select>
          </label>
          <label class="pay-field">บัญชีเงินสด/ธนาคาร<input type="text" name="cashBankAccount" placeholder="กสิกร xxx-x-x1234" /></label>
          <label class="pay-field">เลขอ้างอิงการชำระ<input type="text" name="paymentRef" /></label>

          <label class="alloc-field">วิธีปันส่วน
            <select name="allocationMethod"><option value="">ไม่ปันส่วน (ลงตรง)</option><option value="direct">กำหนดตรง</option><option value="percentage">เปอร์เซ็นต์</option><option value="revenue">ตามรายได้</option><option value="orders">ตามจำนวนออเดอร์</option><option value="units">ตามจำนวนชิ้น</option></select>
          </label>
          <label class="alloc-field">ศูนย์ต้นทุน/แคมเปญ<input type="text" name="costCenter" placeholder="สำนักงานใหญ่" /></label>
          <label>เหตุผล/หมายเหตุ (บังคับเมื่องวดปิด)<input type="text" name="reason" placeholder="เช่น ปรับปรุงตามใบกำกับ" /></label>

          <div class="form-actions">
            <button type="submit">บันทึกรายการ</button>
            <span id="form-result" class="muted"></span>
          </div>
        </form>
        <div id="entry-note" class="foot-note"></div>
        <div class="foot-note">ยอดค้างจ่าย (ยอดรวม − จ่ายจริง): <strong id="f-outstanding">฿0.00</strong></div>
      </div>`;
  },
  mount(root) {
    const form = root.querySelector<HTMLFormElement>('#entry-form')!;
    const cls = root.querySelector<HTMLSelectElement>('#f-class')!;
    const catWrap = root.querySelector<HTMLElement>('#cat-wrap')!;
    const note = root.querySelector<HTMLElement>('#entry-note')!;
    const base = root.querySelector<HTMLInputElement>('#f-base')!;
    const vat = root.querySelector<HTMLInputElement>('#f-vat')!;
    const wht = root.querySelector<HTMLInputElement>('#f-wht')!;
    const total = root.querySelector<HTMLInputElement>('#f-total')!;
    const paidAmt = root.querySelector<HTMLInputElement>('#f-paid-amt')!;
    const outstanding = root.querySelector<HTMLElement>('#f-outstanding')!;
    const result = root.querySelector<HTMLElement>('#form-result')!;

    const num = (el: HTMLInputElement): number => Number(el.value) || 0;
    const recompute = (): void => {
      const t = num(base) + num(vat);
      total.value = t.toFixed(2);
      outstanding.textContent = formatTHB(t - num(paidAmt));
    };
    const show = (selector: string, visible: boolean): void => {
      root.querySelectorAll<HTMLElement>(selector).forEach((el) => {
        el.hidden = !visible;
      });
    };
    const onClassChange = (): void => {
      const v = cls.value;
      const isTax = v === 'tax_only';
      catWrap.hidden = v === 'income' || isTax || v === 'asset';
      show('.cp-field', v !== 'adjustment');
      show('.doc-field', v !== 'adjustment' && v !== 'reversal');
      show('.tax-field', v === 'expense' || v === 'income' || isTax);
      show('.pay-field', v !== 'tax_only' && v !== 'reversal');
      show('.alloc-field', v === 'expense');
      note.innerHTML = isTax
        ? 'ℹ️ รายการภาษี — ไม่ลดกำไรอัตโนมัติ (pnl_impact = false จนกว่าฝ่ายบัญชีจะยืนยัน)'
        : v === 'asset'
          ? 'ℹ️ การซื้อที่เป็นทรัพย์สินควรบันทึกที่หน้า “ทรัพย์สิน” เพื่อเข้าคิวตรวจสอบและคิดค่าเสื่อม'
          : v === 'adjustment' || v === 'reversal'
            ? 'ℹ️ ใช้สำหรับแก้ไขงวดที่ปิดแล้ว — ต้องระบุเหตุผล'
            : '';
    };

    base.addEventListener('input', () => {
      vat.value = (num(base) * 0.07).toFixed(2);
      recompute();
    });
    [vat, wht, paidAmt].forEach((el) => el.addEventListener('input', recompute));
    cls.addEventListener('change', onClassChange);
    recompute();
    onClassChange();

    const field = (name: string): string =>
      (form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | null)?.value.trim() ??
      '';

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const body: Record<string, unknown> = {
        classification: field('classification'),
        totalAmount: total.value || '0',
      };
      for (const name of [
        'documentDate', 'eventDate', 'accountingDate', 'paymentDate',
        'counterpartyName', 'counterpartyType', 'counterpartyTaxId', 'counterpartyBranch',
        'documentType', 'invoiceNo', 'receiptNo',
        'amountBeforeVat', 'vatAmount', 'whtAmount', 'paidAmount',
        'vatBasis', 'vatClaimability', 'whtType',
        'paymentMethod', 'cashBankAccount', 'paymentRef',
        'allocationMethod', 'costCenter', 'reason',
      ]) {
        const v = field(name);
        if (v) body[name] = v;
      }
      if (!catWrap.hidden && field('pnlCategory')) body['pnlCategory'] = field('pnlCategory');
      if (field('whtRate')) body['whtRate'] = Number(field('whtRate'));

      if (!getTenant()) {
        result.className = 'muted';
        result.innerHTML = `ตัวอย่าง (โหมด demo): ${esc(String(body['classification']))} · ยอดรวม ${total.value} — เข้าสู่ระบบเพื่อบันทึกจริง`;
        return;
      }
      result.className = 'muted';
      result.textContent = 'กำลังบันทึก…';
      createEntry(body)
        .then((r) => {
          result.className = 'muted';
          result.innerHTML = `บันทึกแล้ว ✓ (<a class="btn-link" href="#entries">ดูในรายการ</a>) รหัส ${esc(r.id)}`;
          form.reset();
          recompute();
          onClassChange();
        })
        .catch((err: unknown) => {
          result.className = 'error';
          result.textContent = err instanceof Error ? err.message : 'บันทึกไม่สำเร็จ';
        });
    });
  },
};

export function loginPage(onSuccess: () => void, onSignup?: () => void): Page {
  return {
    title: 'เข้าสู่ระบบ',
    render() {
      const demoEmail = config.demoHints ? 'owner-a@demo.local' : '';
      const demoPass = config.demoHints ? 'demo1234' : '';
      const hint = config.demoHints
        ? `<p class="foot-note">demo user: owner-a@demo.local / demo1234</p>`
        : '';
      const signupLink = onSignup
        ? `<p class="foot-note auth-switch">ยังไม่มีบัญชี? <a href="#" id="go-signup">สมัครใช้งานใหม่</a></p>`
        : '';
      return `
        <div class="login-wrap">
          <div class="card" style="max-width:380px;margin:60px auto">
            <h3>เข้าสู่ระบบ RRP P&amp;L</h3>
            <form id="login-form" class="form">
              <label>อีเมล<input type="email" id="l-email" value="${demoEmail}" required /></label>
              <label>รหัสผ่าน<input type="password" id="l-pass" value="${demoPass}" required /></label>
              <button type="submit" class="btn">เข้าสู่ระบบ</button>
              <div id="l-err" class="foot-note" style="color:var(--neg)"></div>
              <p class="foot-note"><a href="#" id="go-reset">ลืมรหัสผ่าน?</a></p>
              ${hint}
              ${signupLink}
            </form>
          </div>
        </div>`;
    },
    mount(root) {
      const err = root.querySelector<HTMLElement>('#l-err')!;
      root.querySelector<HTMLFormElement>('#login-form')!.addEventListener('submit', async (e) => {
        e.preventDefault();
        err.textContent = 'กำลังเข้าสู่ระบบ...';
        try {
          await signIn(
            root.querySelector<HTMLInputElement>('#l-email')!.value,
            root.querySelector<HTMLInputElement>('#l-pass')!.value,
          );
          onSuccess();
        } catch (ex) {
          err.textContent = ex instanceof Error ? ex.message : 'เข้าสู่ระบบไม่สำเร็จ';
        }
      });
      root.querySelector<HTMLAnchorElement>('#go-signup')?.addEventListener('click', (e) => {
        e.preventDefault();
        onSignup?.();
      });
      root.querySelector<HTMLAnchorElement>('#go-reset')?.addEventListener('click', async (e) => {
        e.preventDefault();
        const email = root.querySelector<HTMLInputElement>('#l-email')!.value.trim();
        if (!email) {
          err.style.color = 'var(--neg)';
          err.textContent = 'กรอกอีเมลก่อน แล้วกด "ลืมรหัสผ่าน?" อีกครั้ง';
          root.querySelector<HTMLInputElement>('#l-email')!.focus();
          return;
        }
        err.style.color = 'var(--muted)';
        err.textContent = 'กำลังส่งลิงก์รีเซ็ต…';
        try {
          await resetPassword(email);
          err.style.color = 'var(--pos)';
          err.textContent = `✓ ส่งลิงก์รีเซ็ตรหัสผ่านไปที่ ${email} แล้ว — ตรวจอีเมล`;
        } catch (ex) {
          err.style.color = 'var(--neg)';
          err.textContent = ex instanceof Error ? ex.message : 'ส่งอีเมลไม่สำเร็จ';
        }
      });
    },
  };
}

/** สมัครสมาชิกใหม่ (SaaS). onAuthed = ได้ session ทันที (auto-confirm); onBackToLogin = กลับหน้า login */
export function signupPage(onAuthed: () => void, onBackToLogin: () => void): Page {
  return {
    title: 'สมัครใช้งาน',
    render() {
      return `
        <div class="login-wrap">
          <div class="card" style="max-width:400px;margin:48px auto">
            <h3>สมัครใช้งาน RRP P&amp;L</h3>
            <p class="foot-note">เริ่มดูกำไรขาดทุนร้านคุณได้ฟรี</p>
            <form id="signup-form" class="form">
              <label>อีเมล<input type="email" id="su-email" required autocomplete="email" /></label>
              <label>รหัสผ่าน (อย่างน้อย 6 ตัว)<input type="password" id="su-pass" minlength="6" required autocomplete="new-password" /></label>
              <label>ยืนยันรหัสผ่าน<input type="password" id="su-pass2" minlength="6" required autocomplete="new-password" /></label>
              <button type="submit" class="btn" id="su-btn">สมัคร</button>
              <div id="su-msg" class="foot-note"></div>
              <p class="foot-note auth-switch">มีบัญชีอยู่แล้ว? <a href="#" id="go-login">เข้าสู่ระบบ</a></p>
            </form>
          </div>
        </div>`;
    },
    mount(root) {
      const msg = root.querySelector<HTMLElement>('#su-msg')!;
      const btn = root.querySelector<HTMLButtonElement>('#su-btn')!;
      root.querySelector<HTMLFormElement>('#signup-form')!.addEventListener('submit', async (e) => {
        e.preventDefault();
        const email = root.querySelector<HTMLInputElement>('#su-email')!.value.trim();
        const pass = root.querySelector<HTMLInputElement>('#su-pass')!.value;
        const pass2 = root.querySelector<HTMLInputElement>('#su-pass2')!.value;
        if (pass !== pass2) {
          msg.className = 'foot-note';
          msg.style.color = 'var(--neg)';
          msg.textContent = 'รหัสผ่านทั้งสองช่องไม่ตรงกัน';
          return;
        }
        btn.disabled = true;
        msg.style.color = 'var(--muted)';
        msg.textContent = 'กำลังสมัคร…';
        try {
          const { needsConfirmation } = await signUp(email, pass);
          if (needsConfirmation) {
            msg.style.color = 'var(--pos)';
            msg.innerHTML = '✓ สมัครแล้ว — ตรวจอีเมลเพื่อยืนยันบัญชี แล้วเข้าสู่ระบบ';
          } else {
            onAuthed();
          }
        } catch (ex) {
          msg.style.color = 'var(--neg)';
          msg.textContent = ex instanceof Error ? ex.message : 'สมัครไม่สำเร็จ';
        } finally {
          btn.disabled = false;
        }
      });
      root.querySelector<HTMLAnchorElement>('#go-login')?.addEventListener('click', (e) => {
        e.preventDefault();
        onBackToLogin();
      });
    },
  };
}

/** ตั้งรหัสผ่านใหม่ (หลังคลิกลิงก์ recovery จากอีเมล) → เข้าแอปเลย */
export function resetPasswordPage(auth: AuthHash, onDone: () => void): Page {
  return {
    title: 'ตั้งรหัสผ่านใหม่',
    render() {
      return `
        <div class="login-wrap">
          <div class="card" style="max-width:400px;margin:48px auto">
            <h3>ตั้งรหัสผ่านใหม่</h3>
            <p class="foot-note">กรอกรหัสผ่านใหม่ที่ต้องการใช้</p>
            <form id="rp-form" class="form">
              <label>รหัสผ่านใหม่ (อย่างน้อย 6 ตัว)<input type="password" id="rp-pass" minlength="6" required autocomplete="new-password" /></label>
              <label>ยืนยันรหัสผ่านใหม่<input type="password" id="rp-pass2" minlength="6" required autocomplete="new-password" /></label>
              <button type="submit" class="btn" id="rp-btn">บันทึกรหัสผ่านใหม่</button>
              <div id="rp-msg" class="foot-note" style="color:var(--neg)"></div>
            </form>
          </div>
        </div>`;
    },
    mount(root) {
      const msg = root.querySelector<HTMLElement>('#rp-msg')!;
      const btn = root.querySelector<HTMLButtonElement>('#rp-btn')!;
      root.querySelector<HTMLFormElement>('#rp-form')!.addEventListener('submit', async (e) => {
        e.preventDefault();
        const pass = root.querySelector<HTMLInputElement>('#rp-pass')!.value;
        const pass2 = root.querySelector<HTMLInputElement>('#rp-pass2')!.value;
        if (pass !== pass2) {
          msg.style.color = 'var(--neg)';
          msg.textContent = 'รหัสผ่านทั้งสองช่องไม่ตรงกัน';
          return;
        }
        btn.disabled = true;
        msg.style.color = 'var(--muted)';
        msg.textContent = 'กำลังบันทึก…';
        try {
          await updatePassword(auth.accessToken, pass, auth.expiresAt);
          onDone();
        } catch (ex) {
          msg.style.color = 'var(--neg)';
          msg.textContent = ex instanceof Error ? ex.message : 'ตั้งรหัสผ่านใหม่ไม่สำเร็จ';
          btn.disabled = false;
        }
      });
    },
  };
}

/** ตั้งค่ากิจการแรก (หลังสมัคร) — สร้าง account + ร้านแรก แล้วเข้าแอป */
export function onboardingPage(onDone: () => void): Page {
  return {
    title: 'ตั้งค่ากิจการ',
    render() {
      return `
        <div class="login-wrap">
          <div class="card" style="max-width:440px;margin:48px auto">
            <h3>ยินดีต้อนรับ! ตั้งค่ากิจการของคุณ</h3>
            <p class="foot-note">สร้างกิจการและร้านแรก เพื่อเริ่มบันทึกกำไรขาดทุน</p>
            <form id="onb-form" class="form">
              <label>ชื่อกิจการ / บริษัท<input type="text" id="onb-account" placeholder="เช่น ร้านของฉัน จำกัด" required /></label>
              <label>ชื่อร้านแรก<input type="text" id="onb-biz" placeholder="เช่น ร้านหลัก" required /></label>
              <button type="submit" class="btn" id="onb-btn">สร้างและเริ่มใช้งาน</button>
              <div id="onb-msg" class="foot-note" style="color:var(--neg)"></div>
            </form>
          </div>
        </div>`;
    },
    mount(root) {
      const msg = root.querySelector<HTMLElement>('#onb-msg')!;
      const btn = root.querySelector<HTMLButtonElement>('#onb-btn')!;
      root.querySelector<HTMLFormElement>('#onb-form')!.addEventListener('submit', async (e) => {
        e.preventDefault();
        const accountName = root.querySelector<HTMLInputElement>('#onb-account')!.value.trim();
        const businessName = root.querySelector<HTMLInputElement>('#onb-biz')!.value.trim();
        if (!accountName || !businessName) return;
        btn.disabled = true;
        msg.style.color = 'var(--muted)';
        msg.textContent = 'กำลังสร้าง…';
        try {
          await onboardingApi(accountName, businessName);
          onDone();
        } catch (ex) {
          msg.style.color = 'var(--neg)';
          msg.textContent = ex instanceof Error ? ex.message : 'สร้างไม่สำเร็จ';
          btn.disabled = false;
        }
      });
    },
  };
}
